/*
|--------------------------------------------------------------------------
| Authenticator-app (TOTP) enrolment - the passkey registration's twin
|--------------------------------------------------------------------------
|
| Drives the createTotp modal (post-login offer AND the Security page's
| "Set up" button) through the shared setSecurity machinery:
|
|   setupTotp   (Identity-Vetted server-side) -> QR + manual key
|   confirmTotp (rides on the vetted setup)   -> channel active
|
| Identity Vetting interplay, exactly like passkey_register.js: when the
| server answers `identity_vetting_required`, the payload is handed to the
| shared IV modal (id-vetting.js) and the ceremony stops here. After the
| User verifies, the IV resume re-runs setupTotp and id-vetting.js hands
| the fresh QR payload to TotpRegister.continueWithSetup().
|
| Dismissal (the "Not now" path) posts to the dedicated dismiss-prompt
| endpoint and only ever touches the `prompt-for-totp` preference.
*/

var TotpRegister = (function (window, document) {

    function modal() {
        return window.jQuery('#createTotpModal');
    }

    function fieldValue(id) {
        var el = document.getElementById(id);

        return el ? el.value : '';
    }

    function csrfToken() {
        return fieldValue('totpRegistrationToken');
    }

    function securityUrl() {
        return fieldValue('totpSecurityUrl');
    }

    function dismissPromptUrl() {
        return fieldValue('totpDismissPromptUrl');
    }

    var currentSlide = 0;

    function slideTo(index) {
        var wrapper = document.querySelector('#createTotpSlideWrapper');
        if (!wrapper) {
            return;
        }

        var slides = wrapper.querySelectorAll('.slide');
        if (index < 0 || index >= slides.length) {
            return;
        }

        for (var i = 0; i < slides.length; i++) {
            slides[i].classList.remove('active');
        }
        slides[index].classList.add('active');
        currentSlide = index;
    }

    function showError(elementId, message) {
        var el = document.getElementById(elementId);
        if (!el) {
            return;
        }

        if (message) {
            el.textContent = message;
            el.classList.remove('hide');
        } else {
            el.classList.add('hide');
        }
    }

    function genericError() {
        return (window.TotpRegisterLang && window.TotpRegisterLang.genericError) || 'Setup failed';
    }

    function post(url, payload) {
        var body = { _token: csrfToken() };
        for (var key in payload) {
            if (Object.prototype.hasOwnProperty.call(payload, key)) {
                body[key] = payload[key];
            }
        }

        return Promise.resolve(window.jQuery.ajax({
            type: 'POST',
            url: url,
            data: body,
            dataType: 'json'
        }));
    }

    /*
    | Busy state is driven PURELY by the `isBusy` class - the CSS in
    | includes/_modal-content.scss (shared with #createPasskeyBtn) hides the label
    | via visibility and shows the spinner with display:flex !important. No inline
    | styles here, for the exact reason documented on the passkey button: the global
    | $.ajax_prototype complete handler (shared/plugin.js) runs
    | `$(".submitText").show(); $(".loading:not(.always_visible)").hide()` on every
    | request - including the Identity Vetting OTP verify mid-ceremony - and would
    | undo any inline toggle. The class idiom (plus `always_visible` on the markup)
    | is immune to it.
    */
    function setBusy(buttonId, state) {
        var button = document.getElementById(buttonId);
        if (!button) {
            return;
        }

        button.classList.toggle('isBusy', !!state);
    }

    /*
    |--------------------------------------------------------------------------
    | The six one-digit confirmation boxes (owner decision: identical UX to the
    | Email/SMS OTP steps). Own `totp-digit` class and own handlers, deliberately
    | NOT the global otp.js `.otp-input` machinery: its auto-submit posts its own
    | payload shape to the form action, which cannot carry `action=confirmTotp`.
    | The sixth digit auto-confirms, exactly like the OTP steps do.
    |--------------------------------------------------------------------------
    */
    function digitInputs() {
        return window.jQuery('#totpConfirmDigits .totp-digit');
    }

    function buildDigits() {
        var container = document.getElementById('totpConfirmDigits');
        if (!container) {
            return;
        }

        var boxes = '';
        for (var i = 0; i < 6; i++) {
            boxes += '<input type="number" maxlength="1" class="totp-digit" autocomplete="one-time-code"/>';
        }
        container.innerHTML = boxes;
    }

    function collectedCode() {
        var code = '';
        digitInputs().each(function () {
            code += String(this.value || '');
        });

        return code;
    }

    function clearDigits(focusFirst) {
        var $inputs = digitInputs();
        $inputs.val('');
        if (focusFirst && $inputs.length) {
            $inputs.first().trigger('focus');
        }
    }

    /**
     * Focus the first digit box once the setup slide is actually on screen. A single
     * deferred attempt is not enough on the Identity-Vetting resume path: there the
     * modal is OPENING at this very moment (reveal animation running - an off-screen
     * input refuses focus) while the IV modal's close can steal focus back. So the
     * focus is asserted repeatedly until it verifiably lands, then stops.
     */
    function focusFirstDigit() {
        var attempts = 0;
        var timer = setInterval(function () {
            attempts++;

            var $first = digitInputs().first();
            if ($first.length && $first.is(':visible')) {
                $first.trigger('focus');

                if (document.activeElement === $first[0]) {
                    clearInterval(timer);

                    return;
                }
            }

            if (attempts >= 20) {
                clearInterval(timer);
            }
        }, 100);
    }

    function bindDigitHandlers() {
        var $doc = window.jQuery(document);

        $doc.on('input', '#totpConfirmDigits .totp-digit', function () {
            var $inputs = digitInputs(),
                index = $inputs.index(this),
                value = String(this.value || '').replace(/\D/g, '');

            if (value.length > 1) {
                // Paste-into-a-box case: spread the digits forward.
                var chars = value.split('');
                for (var i = 0; i < chars.length && index + i < $inputs.length; i++) {
                    $inputs.eq(index + i).val(chars[i]);
                }
                $inputs.eq(Math.min(index + chars.length, $inputs.length - 1)).trigger('focus');
            } else {
                this.value = value;
                if (value && index < $inputs.length - 1) {
                    $inputs.eq(index + 1).trigger('focus');
                }
            }

            if (collectedCode().length === $inputs.length) {
                confirm();
            }
        });

        $doc.on('keydown', '#totpConfirmDigits .totp-digit', function (event) {
            var $inputs = digitInputs(),
                index = $inputs.index(this);

            if (event.key === 'Backspace' && !this.value && index > 0) {
                $inputs.eq(index - 1).trigger('focus');
            }
        });

        $doc.on('paste', '#totpConfirmDigits .totp-digit', function (event) {
            event.preventDefault();

            var paste = ((event.originalEvent || event).clipboardData.getData('text') || '').replace(/\D/g, '');
            if (!paste) {
                return;
            }

            var $inputs = digitInputs();
            for (var i = 0; i < $inputs.length; i++) {
                $inputs.eq(i).val(paste.charAt(i) || '');
            }
            $inputs.eq(Math.min(paste.length, $inputs.length - 1)).trigger('focus');

            if (collectedCode().length === $inputs.length) {
                confirm();
            }
        });
    }

    /**
     * Render the vetted setup payload (inline SVG QR + manual key) into the setup
     * slide. The material lives only in this DOM - never in any page source.
     */
    function renderSetup(totp) {
        var qr = document.getElementById('totpQrContainer');
        var key = document.getElementById('totpManualKey');

        if (qr) {
            qr.innerHTML = totp && totp.qr_svg ? totp.qr_svg : '';
        }
        if (key) {
            key.textContent = totp && totp.secret ? totp.secret : '';
        }

        buildDigits();
    }

    /**
     * The setupTotp request. Vetting-gated server-side: an unvetted User gets the
     * standard IV payload instead of the QR - hand it to the shared IV flow and stop;
     * the resume delivers the QR through continueWithSetup() below.
     *
     * Errors land on whichever slide the User is looking at: the offer slide in the
     * post-login flow, the setup slide when launched from the Security tab (which
     * jumps straight to the QR slide and never shows the offer).
     */
    function setupErrorElement() {
        return openedForSetup ? 'confirmTotpErrorMessage' : 'createTotpErrorMessage';
    }

    function setup() {
        showError(setupErrorElement(), null);
        setBusy('setupTotpBtn', true);

        return post(securityUrl(), { action: 'setupTotp' })
            .then(function (data) {
                if (data && data.data && data.data.identity_vetting_required) {
                    if (typeof window.checkForIdentityVetting === 'function') {
                        window.checkForIdentityVetting(data);
                    }

                    return null;
                }

                var totp = data && data.data && data.data.result && data.data.result.totp;
                if (data && data.success && totp && totp.pending) {
                    renderSetup(totp);
                    slideTo(1);
                    focusFirstDigit();

                    return null;
                }

                showError(setupErrorElement(), (data && data.msg) || genericError());

                return null;
            })
            .catch(function (xhr) {
                var msg = xhr && xhr.responseJSON && xhr.responseJSON.msg;
                showError(setupErrorElement(), msg || genericError());

                return null;
            })
            .then(function () {
                setBusy('setupTotpBtn', false);
            });
    }

    /**
     * IV resume entry point: id-vetting.js calls this with the fresh QR payload after
     * the User verified - the twin of PasskeyRegister.continueWithOptions().
     */
    function continueWithSetup(totp) {
        var $modal = modal();
        if ($modal.length && !$modal.hasClass('open') && typeof $modal.modal_open === 'function') {
            $modal.modal_open();
        }

        renderSetup(totp);
        slideTo(1);
        focusFirstDigit();
    }

    var confirmInFlight = false;

    // Set when the ceremony completed in THIS page view: closing the modal then
    // refreshes the Security tab (owner call - the page behind still shows the
    // pre-enrolment "Not set up" row otherwise). Account page only: the post-login
    // homepage offer has no stale row behind it, so it just closes.
    var enrolledThisPageView = false;

    function confirm() {
        // The sixth digit auto-confirms AND the button stays clickable - never let the
        // two race into a double submit (the second would burn a replay-guarded code).
        if (confirmInFlight) {
            return;
        }
        confirmInFlight = true;

        showError('confirmTotpErrorMessage', null);
        setBusy('confirmTotpBtn', true);

        return post(securityUrl(), { action: 'confirmTotp', totp_code: collectedCode() })
            .then(function (data) {
                if (data && data.success) {
                    enrolledThisPageView = true;
                    slideTo(3);

                    return null;
                }

                showError('confirmTotpErrorMessage', (data && data.msg) || genericError());
                clearDigits(true);

                return null;
            })
            .catch(function (xhr) {
                var msg = xhr && xhr.responseJSON && xhr.responseJSON.msg;
                showError('confirmTotpErrorMessage', msg || genericError());
                clearDigits(true);

                return null;
            })
            .then(function () {
                confirmInFlight = false;
                setBusy('confirmTotpBtn', false);
            });
    }

    /**
     * "Never ask again?" answer -> the dedicated preference endpoint, then close.
     */
    function dismiss(neverAskAgain) {
        return post(dismissPromptUrl(), { never_ask_again: neverAskAgain ? 1 : 0 })
            .catch(function () {
                return null;
            })
            .then(function () {
                var $modal = modal();
                if ($modal.length && typeof $modal.modal_close === 'function') {
                    $modal.modal_close();
                }
            });
    }

    /*
    |--------------------------------------------------------------------------
    | Removal - the passkey-delete twin. The createTotp modal (and its hidden
    | URL/token inputs) is only rendered while there is NOTHING to delete, so
    | the endpoint comes from the always-present `account_security` global and
    | the token from the always-rendered delete-totp confirm modal.
    |--------------------------------------------------------------------------
    */
    var deleteBusy = false;

    function deleteEndpoint() {
        if (typeof account_security !== 'undefined' && account_security) {
            return account_security;
        }

        return securityUrl();
    }

    function deleteCsrfToken() {
        var el = document.getElementById('totpDeleteToken');
        if (el && el.value) {
            return el.value;
        }
        var globalToken = window.jQuery ? window.jQuery('[name="_token"]').val() : null;

        return globalToken || csrfToken();
    }

    function deleteConfirmBusy(state) {
        if (!window.jQuery) {
            return;
        }
        var $b = window.jQuery('#delete-totp-confirm .totpDeleteConfirm');
        if (!$b.length) {
            return;
        }

        if (state) {
            // Lock BOTH dimensions BEFORE hiding the label (the passkey delete-confirm
            // technique), so the button keeps its exact size with the spinner centred
            // instead of collapsing to the spinner's width/height.
            $b.css({
                'width': $b.outerWidth() + 'px',
                'height': $b.outerHeight() + 'px',
                'display': 'inline-flex',
                'align-items': 'center',
                'justify-content': 'center'
            });
            $b.addClass('disabled');
            $b.find('.submitText').addClass('hide');
            $b.find('.loading').removeClass('hide').css('display', '');
        } else {
            $b.css({ 'width': '', 'height': '', 'display': '', 'align-items': '', 'justify-content': '' });
            $b.removeClass('disabled');
            $b.find('.submitText').removeClass('hide');
            $b.find('.loading').addClass('hide').css('display', 'none');
        }
    }

    function confirmDelete() {
        if (deleteBusy) {
            return;
        }
        deleteBusy = true;
        deleteConfirmBusy(true);

        Promise.resolve(window.jQuery.ajax({
            type: 'POST',
            url: deleteEndpoint(),
            data: { _token: deleteCsrfToken(), action: 'deleteTotp' },
            dataType: 'json'
        }))
            .then(function (data) {
                if (data && data.data && data.data.identity_vetting_required) {
                    if (typeof window.checkForIdentityVetting === 'function') {
                        window.checkForIdentityVetting(data);
                    }
                    var ivError = new Error('identity_vetting_required');
                    ivError.identityVetting = true;
                    throw ivError;
                }

                if (!data || !data.success) {
                    var err = new Error((data && data.msg) || genericError());
                    err.responseJSON = data;
                    throw err;
                }

                // Removed. Reload TO the #securityContent hash so the Security tab stays
                // active, exactly as the passkey delete does.
                try { window.location.hash = 'securityContent'; } catch (e) {}
                window.location.reload();
            })
            .catch(function (error) {
                deleteBusy = false;
                deleteConfirmBusy(false);

                if (window.jQuery) {
                    window.jQuery('#delete-totp-confirm').modal_close();
                }

                if (error && error.identityVetting) {
                    // IV modal is now showing; the delete resumes after verification.
                    return;
                }

                var message = (error && error.responseJSON && error.responseJSON.msg) || (error && error.message) || genericError();
                if (window.jQuery && typeof window.jQuery.alertHandler === 'function' && typeof alert_box_failure !== 'undefined') {
                    window.jQuery.alertHandler('', message, alert_box_failure);
                }
            });
    }

    // TRUE when the ceremony was launched from the Security tab's "Set up" button.
    // In that context the offer and "stop asking?" slides are meaningless (the user
    // explicitly asked for the ceremony - owner call), so the setup slide's back
    // button becomes a plain Cancel that closes the modal gracefully instead of
    // returning to the offer. The abandoned UNCONFIRMED secret is harmless by
    // design: it is not an active channel, and the next setup replaces it.
    var openedForSetup = false;

    function setupBack() {
        if (openedForSetup) {
            var $modal = modal();
            if ($modal.length && typeof $modal.modal_close === 'function') {
                $modal.modal_close();
            }

            return;
        }

        slideTo(0);
    }

    function setupBackLabel() {
        var button = document.getElementById('totpSetupBackBtn');
        if (!button) {
            return;
        }

        var lang = window.TotpRegisterLang || {};
        var label = openedForSetup ? lang.cancel : lang.backToOffer;
        if (label) {
            button.textContent = label;
        }
    }

    /**
     * The Security page's "Set up" button: open the modal and go straight to the
     * ceremony (the offer slide is the post-login prompt's job, not this button's).
     */
    /**
     * Busy state for the Security tab's pencil affordance: a spinner takes the
     * pencil's place (owner call), with the anchor's dimensions locked FIRST so
     * nothing shifts or collapses. (The infamous earlier "vanishing" was never the
     * spinner - the edit_btn magic class was hiding the whole row block.)
     */
    function setInlineBusy(state) {
        if (!window.jQuery) {
            return;
        }
        var $b = window.jQuery('#setupTotpInlineBtn');
        if (!$b.length) {
            return;
        }

        if (state) {
            $b.css({
                'width': $b.outerWidth() + 'px',
                'height': $b.outerHeight() + 'px',
                'display': 'inline-flex',
                'align-items': 'center',
                'justify-content': 'center',
                'pointer-events': 'none'
            });
            $b.find('i').addClass('hide');
            $b.find('.loading').removeClass('hide').css('display', '');
        } else {
            $b.css({ 'width': '', 'height': '', 'display': '', 'align-items': '', 'justify-content': '', 'pointer-events': '' });
            $b.find('i').removeClass('hide');
            $b.find('.loading').addClass('hide').css('display', 'none');
        }
    }

    /*
    | The Security tab's "Set up" button. NOTHING opens until the server says the
    | ceremony may proceed (owner call: a modal that appears and vanishes into the
    | IV modal two seconds later is confusing). The button itself goes busy while
    | setupTotp runs; if Identity Vetting is required, the IV modal is the FIRST
    | and only thing the User sees - the QR modal opens after, via the vetting
    | resume (continueWithSetup). When the session is already vetted, the response
    | carries the QR material directly and the modal opens once, ready.
    */
    function openForSetup() {
        openedForSetup = true;
        setupBackLabel();
        setInlineBusy(true);

        return post(securityUrl(), { action: 'setupTotp' })
            .then(function (data) {
                if (data && data.data && data.data.identity_vetting_required) {
                    if (typeof window.checkForIdentityVetting === 'function') {
                        window.checkForIdentityVetting(data);
                    }

                    return null;
                }

                var totp = data && data.data && data.data.result && data.data.result.totp;
                if (data && data.success && totp && totp.pending) {
                    continueWithSetup(totp);

                    return null;
                }

                var message = (data && data.msg) || genericError();
                if (window.jQuery && typeof window.jQuery.alertHandler === 'function' && typeof alert_box_failure !== 'undefined') {
                    window.jQuery.alertHandler('', message, alert_box_failure);
                }

                return null;
            })
            .catch(function (xhr) {
                var message = (xhr && xhr.responseJSON && xhr.responseJSON.msg) || genericError();
                if (window.jQuery && typeof window.jQuery.alertHandler === 'function' && typeof alert_box_failure !== 'undefined') {
                    window.jQuery.alertHandler('', message, alert_box_failure);
                }

                return null;
            })
            .then(function () {
                setInlineBusy(false);
            });
    }

    function bind() {
        var setupBtn = document.getElementById('setupTotpBtn');
        if (setupBtn) {
            setupBtn.addEventListener('click', function () {
                setup();
            });
        }

        var confirmBtn = document.getElementById('confirmTotpBtn');
        if (confirmBtn) {
            confirmBtn.addEventListener('click', function () {
                confirm();
            });
        }

        bindDigitHandlers();

        window.jQuery(document).on('click', '.dismiss_totp_prompt', function (event) {
            event.preventDefault();
            dismiss(window.jQuery(this).data('never-ask-again') == 1);
        });

        // Closing the modal after a completed enrolment refreshes the Security tab so
        // the "Authenticator app" row shows its new state - the exact reload-to-hash
        // idiom the delete flow uses. `account_security` (show.blade global) is the
        // "we are on the account page" signal, same as deleteEndpoint() relies on.
        window.jQuery(document).on('click', '#createTotpModal .close-reveal-mymodal', function () {
            if (enrolledThisPageView && typeof account_security !== 'undefined' && account_security) {
                try { window.location.hash = 'securityContent'; } catch (e) {}
                window.location.reload();
            }
        });

        // Belt and braces: whatever modal of this flow closes - the QR ceremony or
        // the Identity Vetting one - and however it closes (X, cancel, backdrop),
        // the pencil affordance is restored. The primary restore is openForSetup's
        // final .then(); this guarantees no path can ever leave it dimmed.
        window.jQuery(document).on('modal:closed', '#createTotpModal, #auth_modal', function () {
            setInlineBusy(false);
        });

        // Security page: Remove -> confirm modal -> the vetted delete.
        window.jQuery(document).on('click', '.totpDeleteBtn', function (event) {
            event.preventDefault();
            window.jQuery('#delete-totp-confirm').modal_open();
        });

        window.jQuery(document).on('click', '.totpDeleteConfirm', function (event) {
            event.preventDefault();
            confirmDelete();
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bind);
    } else {
        bind();
    }

    return {
        slideTo: slideTo,
        setup: setup,
        setupBack: setupBack,
        confirm: confirm,
        continueWithSetup: continueWithSetup,
        openForSetup: openForSetup,
        confirmDelete: confirmDelete,
        dismiss: dismiss
    };

})(window, document);

window.TotpRegister = TotpRegister;
